/**
 * Invite send decision shared by manual, CSV, API, and Zapier.
 * Zapier uses the API source. Suppression is by email address, not by agent.
 */

const REMINDER_GAP_MS = 24 * 60 * 60 * 1000;
const MAX_SUCCESSFUL_SENDS = 2;

const INVITE_SOURCES = ['manual', 'csv', 'api', 'zapier', 'resend_button', 'pending_button'];

function normalizeInviteEmail(email) {
  return String(email || '').trim().toLowerCase();
}

/** Invites cannot go out without a working one-click unsubscribe link. */
function missingUnsubscribeSecret(env = process.env) {
  return String((env && env.UNSUBSCRIBE_SECRET) || '').trim() ? null : 'unsubscribe_not_configured';
}

/** The footer has to name a real postal address. There is no placeholder. */
function missingPostalAddress(env = process.env) {
  return String((env && env.PACARION_POSTAL_ADDRESS) || '').trim() ? null : 'postal_address_not_configured';
}

/**
 * @returns {null | { invite_status: string, error: string, log: string | null }}
 * A non-null result means do not send. source is recorded by the caller and
 * never bypasses this check.
 */
function passwordIsMissing(passwordHash) {
  return passwordHash == null || String(passwordHash).trim() === '';
}

/**
 * A homeowner who already counts as joined, but never saved a password,
 * can be given a new invite code. Callers that do not pass a password
 * signal keep the joined block.
 */
function shouldIssueFreshInviteCode({
  clientStatus,
  inviteStatus,
  hasPassword,
  passwordHash,
} = {}) {
  const joined = clientStatus === 'active' || inviteStatus === 'accepted';
  if (!joined || inviteStatus === 'suppressed') return false;
  if (hasPassword === true) return false;
  if (hasPassword === false) return true;
  if (passwordHash === undefined) return false;
  return passwordIsMissing(passwordHash);
}

/** 'send' for a normal invite, 'fresh' for a new code, or null to hide the action. */
function showInviteSendAction(client) {
  if (!client || client.invite_status === 'suppressed') return null;
  const joined = client.status === 'active' || client.invite_status === 'accepted';
  if (!joined) return 'send';
  if (client.has_password === true) return null;
  if (typeof client.password_hash === 'string' && /^\$2[aby]\$/.test(client.password_hash)) return null;
  if (client.has_password === false) return 'fresh';
  if (passwordIsMissing(client.password_hash)) return 'fresh';
  return null;
}

function inviteBlockReason({
  email,
  suppressionReasons,
  inviteStatus,
  clientStatus,
  inviteSendCount,
  inviteLastSentAt,
  hasPassword,
  passwordHash,
  now = Date.now(),
}) {
  const normalized = normalizeInviteEmail(email);
  const listed = suppressionReasons && typeof suppressionReasons.get === 'function'
    ? suppressionReasons.get(normalized)
    : undefined;
  if (listed || inviteStatus === 'suppressed') {
    return {
      invite_status: 'suppressed',
      error: listed || 'unsubscribed',
      log: 'invite_suppressed',
    };
  }
  if ((clientStatus === 'active' || inviteStatus === 'accepted')
    && !shouldIssueFreshInviteCode({ clientStatus, inviteStatus, hasPassword, passwordHash })) {
    return { invite_status: 'accepted', error: 'already_joined', log: null };
  }
  const sent = Number(inviteSendCount) || 0;
  if (sent >= MAX_SUCCESSFUL_SENDS) {
    return { invite_status: inviteStatus || 'sent', error: 'resend_limit', log: null };
  }
  if (sent >= 1 && inviteLastSentAt) {
    const elapsed = now - new Date(inviteLastSentAt).getTime();
    if (!Number.isNaN(elapsed) && elapsed < REMINDER_GAP_MS) {
      return { invite_status: inviteStatus || 'sent', error: 'resend_wait', log: null };
    }
  }
  return null;
}

export {
  REMINDER_GAP_MS,
  MAX_SUCCESSFUL_SENDS,
  INVITE_SOURCES,
  normalizeInviteEmail,
  missingUnsubscribeSecret,
  missingPostalAddress,
  passwordIsMissing,
  shouldIssueFreshInviteCode,
  showInviteSendAction,
  inviteBlockReason,
};
